import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/lib/errors';
import { backfillSingleCandidateFromPf } from '@/lib/peopleforce';
import {
  addCandidateSkill,
  addCandidateSkillFromVacancy,
  addCandidateTag,
  anonymiseCandidate,
  archiveCandidate,
  hardDeleteCandidate,
  assignCandidate,
  cancelCandidateInterview,
  checkCandidateDuplicates,
  commitFromCv,
  createCandidate,
  createCandidateInterview,
  createCandidateNote,
  deleteCandidateFile,
  deleteCandidateNote,
  getCandidate,
  getCandidateAssignments,
  getCandidateAuditLog,
  getCandidateConsentTimeline,
  getCandidateFiles,
  getCandidateInterviews,
  getCandidateLocations,
  getCandidateNotes,
  getCandidateTags,
  getCandidateVacancyMatch,
  getCandidates,
  getParseJob,
  grantOfflineCandidateConsent,
  parseCvForCandidate,
  parseCvStandalone,
  removeCandidateSkill,
  removeCandidateTag,
  requestCandidateConsent,
  revokeCandidateConsent,
  setCandidateSkillVerification,
  updateCandidate,
  updateCandidateAssignment,
  updateCandidateNote,
  updateCandidateInterview,
  updateCandidateStatus,
  uploadCandidateFile,
} from '@/lib/candidates';
import type {
  AssignCandidateInput,
  AuditEventType,
  CandidateDetail,
  CandidateFileType,
  CandidateQueryParams,
  CandidateStatus,
  CreateCandidateNoteInput,
  CreateInterviewInput,
  DuplicateCheckInput,
  FromCvInput,
  GrantOfflineConsentInput,
  InterviewScope,
  ParseJob,
  RequestConsentInput,
  RevokeConsentInput,
  UpdateAssignmentInput,
  UpdateCandidateInput,
  UpdateCandidateNoteInput,
  UpdateInterviewInput,
} from '@/types/candidates';

const STALE_TIME = 30_000;

export const candidateKeys = {
  all: ['recruitment', 'candidates'] as const,
  lists: () => [...candidateKeys.all, 'list'] as const,
  list: (params: CandidateQueryParams) => [...candidateKeys.all, 'list', params] as const,
  detail: (id: string) => [...candidateKeys.all, 'detail', id] as const,
  locations: () => [...candidateKeys.all, 'locations'] as const,
  tags: () => [...candidateKeys.all, 'tags'] as const,
  auditLog: (id: string, types: AuditEventType[]) =>
    [...candidateKeys.all, 'auditLog', id, types] as const,
  notes: (id: string) => [...candidateKeys.all, 'notes', id] as const,
  recentNotes: (id: string) => [...candidateKeys.all, 'recentNotes', id] as const,
  assignments: (id: string) => [...candidateKeys.all, 'assignments', id] as const,
  interviews: (id: string, scope: InterviewScope) =>
    [...candidateKeys.all, 'interviews', id, scope] as const,
  files: (id: string) => [...candidateKeys.all, 'files', id] as const,
  consentTimeline: (id: string) => [...candidateKeys.all, 'consentTimeline', id] as const,
  parseJob: (parseJobId: string) => [...candidateKeys.all, 'parseJob', parseJobId] as const,
  vacancyMatch: (candidateId: string, vacancyId: string) =>
    [...candidateKeys.all, 'vacancyMatch', candidateId, vacancyId] as const,
};

export function useCandidates(params: CandidateQueryParams = {}) {
  return useQuery({
    queryKey: candidateKeys.list(params),
    queryFn: () => getCandidates(params),
    staleTime: STALE_TIME,
  });
}

export function useCandidate(id: string) {
  return useQuery({
    queryKey: candidateKeys.detail(id),
    queryFn: () => getCandidate(id),
    staleTime: STALE_TIME,
    enabled: !!id,
  });
}

export function useCandidateLocations() {
  return useQuery({
    queryKey: candidateKeys.locations(),
    queryFn: getCandidateLocations,
    staleTime: 5 * 60_000,
  });
}

export function useCandidateTags() {
  return useQuery({
    queryKey: candidateKeys.tags(),
    queryFn: getCandidateTags,
    staleTime: 5 * 60_000,
  });
}

export function useAssignCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssignCandidateInput }) =>
      assignCandidate(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.assignments(variables.id) });
    },
  });
}

export function useCandidateAssignments(id: string) {
  return useQuery({
    queryKey: candidateKeys.assignments(id),
    queryFn: () => getCandidateAssignments(id),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useUpdateCandidateAssignment(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: UpdateAssignmentInput;
    }) => updateCandidateAssignment(candidateId, assignmentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.assignments(candidateId) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
      queryClient.invalidateQueries({
        queryKey: [...candidateKeys.all, 'auditLog', candidateId],
      });
    },
  });
}

export function useCreateCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCandidate,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
    },
  });
}

export function useCheckCandidateDuplicates() {
  return useMutation({
    mutationFn: (input: DuplicateCheckInput) => checkCandidateDuplicates(input),
  });
}

/**
 * Reactive duplicate-check for live inputs (debounced name+phone / blurred
 * email). Unlike useCheckCandidateDuplicates (which is a one-shot mutation),
 * this hook keys on the input — when the input changes, TanStack Query
 * cancels the in-flight request via AbortSignal and refetches for the new
 * key, so the UI can't latch onto a stale response.
 */
export function useCandidateDuplicateCheck(
  input: DuplicateCheckInput | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...candidateKeys.all, 'duplicateCheck', input] as const,
    queryFn: ({ signal }) => checkCandidateDuplicates(input!, signal),
    enabled: enabled && !!input,
    staleTime: STALE_TIME,
  });
}

export function useArchiveCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveCandidate(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
    },
  });
}

export function useHardDeleteCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hardDeleteCandidate(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: candidateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
    },
  });
}

export function useUpdateCandidateStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: CandidateStatus) => updateCandidateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
    },
  });
}

export function useUpdateCandidate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCandidateInput) => updateCandidate(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
    },
  });
}

export function useAddCandidateSkill(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => addCandidateSkill(id, name),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
    },
  });
}

export function useSetCandidateSkillVerification(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      skillLinkId,
      verified,
    }: {
      skillLinkId: string;
      verified: boolean;
    }) => setCandidateSkillVerification(candidateId, skillLinkId, verified),
    onMutate: async ({ skillLinkId, verified }) => {
      await queryClient.cancelQueries({ queryKey: candidateKeys.detail(candidateId) });
      const previous = queryClient.getQueryData<CandidateDetail>(
        candidateKeys.detail(candidateId)
      );
      if (previous) {
        queryClient.setQueryData<CandidateDetail>(candidateKeys.detail(candidateId), {
          ...previous,
          skills: previous.skills.map((s) =>
            s.id === skillLinkId ? { ...s, verified } : s
          ),
        });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(candidateKeys.detail(candidateId), ctx.previous);
      }
      toast.error(extractErrorMessage(err, 'Failed to update skill verification'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
    },
  });
}

export function useRemoveCandidateSkill(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (skillLinkId: string) => removeCandidateSkill(id, skillLinkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: [...candidateKeys.all, 'vacancyMatch', id],
      });
    },
  });
}

export function useCandidateVacancyMatch(candidateId: string, vacancyId: string | null) {
  return useQuery({
    queryKey: candidateKeys.vacancyMatch(candidateId, vacancyId ?? ''),
    queryFn: () => getCandidateVacancyMatch(candidateId, vacancyId!),
    enabled: Boolean(candidateId && vacancyId),
    staleTime: STALE_TIME,
  });
}

export function useAddSkillFromVacancy(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, vacancyId }: { skillId: string; vacancyId: string }) =>
      addCandidateSkillFromVacancy(candidateId, skillId, vacancyId),
    onSuccess: (_data, { vacancyId }) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
      queryClient.invalidateQueries({
        queryKey: candidateKeys.vacancyMatch(candidateId, vacancyId),
      });
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
    },
  });
}

export function useAddCandidateTag(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => addCandidateTag(id, name),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.tags() });
    },
  });
}

export function useRemoveCandidateTag(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => removeCandidateTag(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.tags() });
    },
  });
}

const NOTES_PAGE_SIZE = 30;
const RECENT_NOTES_LIMIT = 3;

export function useCandidateNotes(id: string) {
  return useInfiniteQuery({
    queryKey: candidateKeys.notes(id),
    queryFn: ({ pageParam }) =>
      getCandidateNotes(id, {
        limit: NOTES_PAGE_SIZE,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useCandidateRecentNotes(id: string) {
  return useQuery({
    queryKey: candidateKeys.recentNotes(id),
    queryFn: () => getCandidateNotes(id, { limit: RECENT_NOTES_LIMIT }),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useCreateCandidateNote(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCandidateNoteInput) => createCandidateNote(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.notes(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.recentNotes(id) });
      queryClient.invalidateQueries({
        queryKey: [...candidateKeys.all, 'auditLog', id],
      });
    },
  });
}

/**
 * Per-candidate "Pull from PeopleForce" — backfills notes + CV for ONE
 * candidate. Returns in 1-3 seconds and immediately invalidates the notes
 * and files queries so the open page re-renders with the fresh data.
 */
export function useBackfillSingleCandidateFromPf(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => backfillSingleCandidateFromPf(id),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.notes(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.recentNotes(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.files(id) });
    },
  });
}

export function useUpdateCandidateNote(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, input }: { noteId: string; input: UpdateCandidateNoteInput }) =>
      updateCandidateNote(id, noteId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.notes(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.recentNotes(id) });
    },
  });
}

export function useDeleteCandidateNote(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => deleteCandidateNote(id, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.notes(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.recentNotes(id) });
    },
  });
}

export function useCandidateInterviews(id: string, scope: InterviewScope = 'upcoming') {
  return useQuery({
    queryKey: candidateKeys.interviews(id, scope),
    queryFn: () => getCandidateInterviews(id, { scope }),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

function invalidateInterviewKeys(queryClient: ReturnType<typeof useQueryClient>, candidateId: string) {
  queryClient.invalidateQueries({
    queryKey: [...candidateKeys.all, 'interviews', candidateId],
  });
  queryClient.invalidateQueries({
    queryKey: [...candidateKeys.all, 'auditLog', candidateId],
  });
}

export function useCreateInterview(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInterviewInput) => createCandidateInterview(candidateId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateInterviewKeys(queryClient, candidateId),
  });
}

// `suppressGlobalToast` is an opt-in: candidate-interview-scheduler.tsx already
// shows its own error and passes true; candidate-interview-item.tsx's quick
// result-change action (complete/no-show/reopen) has no handling of its own
// and omits it, so the global fallback covers that path.
export function useUpdateInterview(
  candidateId: string,
  opts?: { suppressGlobalToast?: boolean },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      interviewId,
      input,
    }: {
      interviewId: string;
      input: UpdateInterviewInput;
    }) => updateCandidateInterview(candidateId, interviewId, input),
    meta: { suppressGlobalToast: opts?.suppressGlobalToast ?? false },
    onSuccess: () => invalidateInterviewKeys(queryClient, candidateId),
  });
}

export function useCancelInterview(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (interviewId: string) => cancelCandidateInterview(candidateId, interviewId),
    onSuccess: () => invalidateInterviewKeys(queryClient, candidateId),
  });
}

function invalidateFileKeys(queryClient: ReturnType<typeof useQueryClient>, candidateId: string) {
  queryClient.invalidateQueries({ queryKey: candidateKeys.files(candidateId) });
  queryClient.invalidateQueries({
    queryKey: [...candidateKeys.all, 'auditLog', candidateId],
  });
  queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
}

export function useCandidateFiles(id: string) {
  return useQuery({
    queryKey: candidateKeys.files(id),
    queryFn: () => getCandidateFiles(id),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useUploadCandidateFile(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, fileType }: { file: File; fileType: CandidateFileType }) =>
      uploadCandidateFile(candidateId, file, fileType),
    meta: { suppressGlobalToast: true },
    onSuccess: () => invalidateFileKeys(queryClient, candidateId),
  });
}

export function useDeleteCandidateFile(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => deleteCandidateFile(candidateId, fileId),
    onSuccess: () => invalidateFileKeys(queryClient, candidateId),
  });
}

function invalidateConsentKeys(
  queryClient: ReturnType<typeof useQueryClient>,
  candidateId: string,
) {
  queryClient.invalidateQueries({ queryKey: candidateKeys.consentTimeline(candidateId) });
  queryClient.invalidateQueries({
    queryKey: [...candidateKeys.all, 'auditLog', candidateId],
  });
  queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
  queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
}

export function useCandidateConsentTimeline(id: string) {
  return useQuery({
    queryKey: candidateKeys.consentTimeline(id),
    queryFn: () => getCandidateConsentTimeline(id),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

export function useRequestCandidateConsent(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestConsentInput = {}) => requestCandidateConsent(candidateId, input),
    onSuccess: () => invalidateConsentKeys(queryClient, candidateId),
  });
}

export function useGrantOfflineCandidateConsent(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GrantOfflineConsentInput) =>
      grantOfflineCandidateConsent(candidateId, input),
    onSuccess: () => invalidateConsentKeys(queryClient, candidateId),
  });
}

export function useRevokeCandidateConsent(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevokeConsentInput) => revokeCandidateConsent(candidateId, input),
    // HW-1055: also on a refusal — a 409 CONSENT_CHANGED means the candidate
    // changed meanwhile, and the card should show what they are now.
    onSettled: () => invalidateConsentKeys(queryClient, candidateId),
  });
}

export function useAnonymiseCandidate(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => anonymiseCandidate(candidateId),
    onSuccess: () => {
      invalidateConsentKeys(queryClient, candidateId);
      queryClient.invalidateQueries({ queryKey: candidateKeys.files(candidateId) });
    },
  });
}

const AUDIT_PAGE_SIZE = 50;

export function useParseCvForCandidate(candidateId: string) {
  return useMutation({
    mutationFn: (file: File) => parseCvForCandidate(candidateId, file),
    meta: { suppressGlobalToast: true },
  });
}

export function useParseCvStandalone() {
  return useMutation({
    mutationFn: (file: File) => parseCvStandalone(file),
    meta: { suppressGlobalToast: true },
  });
}

export function useParseJobStatus(parseJobId: string | null | undefined) {
  return useQuery<ParseJob>({
    queryKey: parseJobId ? candidateKeys.parseJob(parseJobId) : ['parseJob', 'disabled'],
    queryFn: () => getParseJob(parseJobId as string),
    enabled: !!parseJobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 1500;
      return data.status === 'QUEUED' || data.status === 'RUNNING' ? 1500 : false;
    },
    staleTime: 0,
  });
}

export function useCommitFromCv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FromCvInput) => commitFromCv(input),
    meta: { suppressGlobalToast: true },
    onSuccess: (candidate) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      queryClient.setQueryData(candidateKeys.detail(candidate.id), candidate);
    },
  });
}

export function useCandidateAuditLog(id: string, types: AuditEventType[] = []) {
  return useInfiniteQuery({
    queryKey: candidateKeys.auditLog(id, types),
    queryFn: ({ pageParam }) =>
      getCandidateAuditLog(id, {
        limit: AUDIT_PAGE_SIZE,
        cursor: pageParam as string | undefined,
        types: types.length > 0 ? types : undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}
